import * as moment from 'moment';
import * as Mutations from './mutations';
import * as PageMutations from '../page/mutations';
import {PaginationParams} from '@/common/utils/pagination-params';
import {MOMENT_BACKEND_DATE_FORMAT} from '@/constants/dateFormats';
import {ActivityDetailsService} from '@/common/services/pages/activity-details.service';

export const AttendanceModule = {
    state: {
        class: {
            list: null,
            statistics: null,
            dates: null,
            checkIn: [],
            checkOut: []
        },
        camp: {
            list: null,
            statistics: null,
            dates: null,
            checkIn: [],
            checkOut: []
        },
        aftercare: {
            list: null,
            statistics: null,
            dates: null,
            checkIn: [],
            checkOut: []
        }
    },

    mutations: {
        [Mutations.SET_ATTENDACE_LIST](state, { activityType, list }) {
            const key = activityType.toLowerCase();
            state[key].list = list;
        },

        [Mutations.SET_ATTENDANCE_STATISTICS](state, { activityType, statistics }) {
            const key = activityType.toLowerCase();
            state[key].statistics = statistics;
        },

        [Mutations.SET_ATTENDANCE_DATES](state, { activityType, dates }) {
            const key = activityType.toLowerCase();
            state[key].dates = dates;
        },

        [Mutations.RESET_ATTENDANCE_DATES](state, activityType) {
            const key = activityType.toLowerCase();
            state[key].dates = null;
        },

        [Mutations.SET_ATTENDANCE_CHECK_IN](state, { activityType, attendanceIdsCheckIn }) {
            const key = activityType.toLowerCase();
            state[key].checkIn = [...new Set([...state[key].checkIn, ...attendanceIdsCheckIn])];

        },

        [Mutations.SET_ATTENDANCE_CHECK_OUT](state, { activityType, attendanceIdsCheckOut }) {
            const key = activityType.toLowerCase();
            state[key].checkOut = [...new Set([...state[key].checkOut, ...attendanceIdsCheckOut])];

        },

        [Mutations.RESET_ATTENDANCE_CHECK_IN](state, activityType) {
            const key = activityType.toLowerCase();
            state[key].checkIn = [];
        },

        [Mutations.RESET_ATTENDANCE_CHECK_OUT](state, activityType) {
            const key = activityType.toLowerCase();
            state[key].checkOut = [];
        }
    },

    actions: {
        async fetchAttendanceList(context, { activityId, activityType, callback }) {
            const attendanceIdsCheckIn = [];
            const attendanceIdsCheckOut = [];
            const { schoolId, showDropIn } = context.getters;
            const { data: { content, pageable, totalPages, totalElements, numberOfElements } } =
                await ActivityDetailsService.getAttendancesList({
                    activityType, schoolId, activityId,
                    params: PaginationParams.createParams({
                        size: context.getters.pageSize,
                        page: context.getters.pageNumber,
                        sort: context.getters.sortBy,
                        direction: context.getters.direction,
                        query: context.getters.searchQuery,
                        filters: {
                            ...context.getters.filters,
                        }
                    })
                });
            context.commit(Mutations.SET_ATTENDACE_LIST, { activityType, list: content });
            activityType === 'Aftercare' && showDropIn && context.dispatch('fetchDatesForCalendarByActivity', { activityId, activityType });
            context.dispatch('configPagination', {
                ...pageable,
                numberOfElements,
                totalElements,
                totalPages,
                callback
            });

            content && content.forEach(item => item.checkIn && attendanceIdsCheckIn.push(item.id));
            context.commit(Mutations.SET_ATTENDANCE_CHECK_IN, { activityType, attendanceIdsCheckIn });

            content && content.forEach(item => item.checkOut && attendanceIdsCheckOut.push(item.id));
            context.commit(Mutations.SET_ATTENDANCE_CHECK_OUT, { activityType, attendanceIdsCheckOut });
        },

        async fetchAttendanceListAndStatistics(context, { activityId, activityType }) {
            const { filters } = context.getters;
            if (!filters || !filters.date) {
                context.commit(PageMutations.SET_FILTER, { date: moment(new Date()).format(MOMENT_BACKEND_DATE_FORMAT)});
            }

            const paginationCallback = () => context.dispatch('fetchAttendanceListAndStatistics', {
                activityId,
                activityType
            });

            await Promise.all([
                context.dispatch('fetchAttendanceList', { activityId, activityType, callback: paginationCallback }),
                context.dispatch('fetchStatisticsByActivity', { activityId, activityType })
            ]);
        },

        async checkIn(context, { activityType, activityId, attendanceIds, date }) {
            const { schoolId, pagination: { callback } } = context.getters;
            const checkIn = date ? moment(date).format('HH:mm') : moment().format('HH:mm');
            await ActivityDetailsService.setActivityAttendanceCheckIn({
                schoolId,
                activityType,
                activityId,
                attendanceIds,
                checkIn
            });
            await callback();
            date && context.dispatch('createSuccessNotification', 'Check-in time is successfully changed');
        },

        async removeCheckIn(context, { activityType, activityId, attendanceId }) {
            const { schoolId, pagination: { callback } } = context.getters;
            await ActivityDetailsService.removeActivityAttendanceCheckIn({
                schoolId,
                activityType,
                activityId,
                attendanceId
            });

            context.dispatch('resetAttendanceCheckInByActivity', activityType);
            if (context.getters.getAttendanceCheckOutByActivity(activityType).includes(attendanceId)) {
                context.dispatch('removeCheckOut', { activityType, activityId, attendanceId })
            } else {
                context.dispatch('resetAttendanceCheckOutByActivity', activityType);
                await callback();
            }

            context.dispatch('createSuccessNotification', 'You have successfully removed check-in time');
        },


        async checkOut(context, { activityType, activityId, attendanceIds, date, checkOutPerson }) {
            const { schoolId, pagination: { callback } } = context.getters;
            const checkOut = date ? moment(date).format('HH:mm') : moment().format('HH:mm');
            await ActivityDetailsService.setActivityAttendanceCheckOut({
                schoolId,
                activityType,
                activityId,
                attendanceIds,
                checkOut,
                checkOutPerson
            });
            await callback();
            date && context.dispatch('createSuccessNotification', 'Check-out time is successfully changed');
        },

        async removeCheckOut(context, { activityType, activityId, attendanceId }) {
            const { schoolId, pagination: { callback } } = context.getters;
            await ActivityDetailsService.removeActivityAttendanceCheckOut({
                schoolId,
                activityType,
                activityId,
                attendanceId
            });
            context.dispatch('resetAttendanceCheckOutByActivity', activityType);
            await callback();
            context.dispatch('createSuccessNotification', 'You have successfully removed check-out time');
        },

        async fetchStatisticsByActivity(context, { activityId, activityType }) {
            const { schoolId, filters: { date } } = context.getters;
            const { data } = await ActivityDetailsService.getAttendanceStatisticsByActivity({
                schoolId,
                activityId,
                activityType,
                date
            });
            context.commit(Mutations.SET_ATTENDANCE_STATISTICS, { activityType, statistics: data });
        },

        async fetchDatesForCalendarByActivity(context, { activityId, activityType }) {
            const { data } = await ActivityDetailsService.getDatesForCalendarByActivity({
                schoolId: context.getters.schoolId,
                activityId,
                activityType
            });
            context.commit(Mutations.SET_ATTENDANCE_DATES, { activityType, dates: data });
        },

        resetAttendanceCheckInByActivity(context, activityType) {
            context.commit(Mutations.RESET_ATTENDANCE_CHECK_IN, activityType);
        },

        resetAttendanceCheckOutByActivity(context, activityType) {
            context.commit(Mutations.RESET_ATTENDANCE_CHECK_OUT, activityType);
        },

        resetAttendanceDatesForFilter(context, activityType) {
            context.commit(Mutations.RESET_ATTENDANCE_DATES, activityType);
        }
    },

    getters: {
        getAttendanceListByActivity: (state, getters, rootState, rootGetters) => (activityType = 'Class') => {
            const key = activityType.toLowerCase();
            const { list } = state[key];
            return list && list
                .map(attendance => ({
                    ...attendance,
                    activityId: attendance.classId || attendance.campPricingId || attendance.aftercareId,
                    birthDate: attendance.birthDate && moment(attendance.birthDate).format(rootGetters.dateFormat.toUpperCase())
                }))
        },

        getAttendanceStatisticsByActivity: state => (activityType = 'Class') => {
            const key = activityType.toLowerCase();
            return state[key] && state[key].statistics
        },

        getAttendanceCheckInByActivity: state => (activityType = 'Class') => {
            const key = activityType.toLowerCase();
            return state[key] && state[key].checkIn
        },

        getAttendanceCheckOutByActivity: state => (activityType = 'Class') => {
            const key = activityType.toLowerCase();
            return state[key] && state[key].checkOut
        },

        getDatesByActivity: state => (activityType = 'Class') => {
            const key = activityType.toLowerCase();
            return state[key] && state[key].dates
        }
    }
};


// WEBPACK FOOTER //
// ./src/store/modules/attendance/attendance.module.js